/**
 * The words half of the capability manifest: for every capability id, what it
 * is called, what it does, what a business uses it for, the question that
 * reveals the need, who can make it today, a draft part that builds it (or one
 * that tries it and is refused), and the closest fit when it cannot be made.
 *
 * `satisfies Record<CapabilityId, CapabilityWords>`: a new id is a compile
 * error until its words are written, and a removed one cannot keep stale
 * words. Not exported from the package on its own; `buildManifest` is the one
 * way in.
 *
 * Rules for the words: plain English a shop owner reads, sentence case,
 * RecordOS's own vocabulary (record, list, entry, field, card, link), and no
 * product's name but RecordOS's (manifest.test.ts enforces it). `reach.setup`
 * is what `authorProblems` accepts today; the phase that widens the draft
 * flips an entry, with its tests. Every `example` uses keys prefixed by its
 * entry, so all of them can sit in one draft together.
 */
import { COMPOSE_LIMITS } from '../compose';
import type { SetupDraftField, SetupDraftLink, SetupDraftRecord } from '../compose-author';
import type { CapabilityId } from './ids';
import type { StudioCardLeaf, StudioLink, StudioLinkCondition, StudioLinkFill, StudioRole } from '../setup-draft/draft';
import type { CapabilityReach, CapabilityWords, SetupDraftFragment, StudioDraftFragment } from './manifest';

/* --------------------------------------------------------------- helpers */

/** Made by a setup today. */
const made = (os: readonly string[], edit: 'yes' | 'no' = 'no'): CapabilityReach => ({ setup: 'yes', edit, os, screen: true });
/** Made only once the workspace exists: by OS, or on a screen. */
const after = (os: readonly string[], screen = true, edit: 'yes' | 'no' = 'no'): CapabilityReach => ({ setup: 'no', edit, os, screen });
/** Made by nothing yet. */
const nothing: CapabilityReach = { setup: 'no', edit: 'no', os: [], screen: false };

const f = (key: string, label: string, type: SetupDraftField['type'], over: Partial<SetupDraftField> = {}): SetupDraftField => ({
  key,
  label,
  type,
  ...over,
});

/** A record whose entries are written each day, headed by a text field. */
const rec = (key: string, name: string, fields: SetupDraftField[], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord => ({
  key,
  name,
  entryNoun: 'entry',
  kind: 'record',
  direction: 'none',
  fields: [f('title', 'Title', 'text'), ...fields],
  headlineFieldKey: 'title',
  ...over,
});

/** A list other records point at, headed by its name. */
const list = (key: string, name: string, fields: SetupDraftField[] = [], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord => ({
  key,
  name,
  entryNoun: 'row',
  kind: 'directory',
  direction: 'none',
  fields: [f('name', 'Name', 'text'), ...fields],
  headlineFieldKey: 'name',
  ...over,
});

/** A record with a day and an amount it totals. */
const sales = (key: string, name: string, fields: SetupDraftField[] = [], over: Partial<SetupDraftRecord> = {}): SetupDraftRecord =>
  rec(key, name, [f('date', 'Date', 'date'), f('amount', 'Amount', 'money'), ...fields], {
    direction: 'in',
    dateFieldKey: 'date',
    measureFieldKey: 'amount',
    ...over,
  });

const one = (...records: SetupDraftRecord[]): SetupDraftFragment => ({ records });

/** Two records and a feed link from the first into the second. */
const linked = (prefix: string, over: Partial<SetupDraftLink> = {}): SetupDraftFragment => ({
  records: [sales(`${prefix}_sales`, 'Sales', [f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }] })]), sales(`${prefix}_cash`, 'Cash in hand')],
  links: [
    {
      fromRecordKey: `${prefix}_sales`,
      toRecordKey: `${prefix}_cash`,
      pattern: 'feed',
      sign: 1,
      when: [{ fieldKey: 'mode', op: 'is', value: 'cash' }],
      carry: [
        { toFieldKey: 'date', fromFieldKey: 'date' },
        { toFieldKey: 'amount', fromFieldKey: 'amount' },
      ],
      ...over,
    },
  ],
});

/** A link with a pattern nothing runs, cast past the draft's type. */
const linkWithPattern = (prefix: string, pattern: string): SetupDraftFragment =>
  linked(prefix, { pattern: pattern as SetupDraftLink['pattern'] });

/** One field of a type with no engine, cast past the draft's type. */
const fieldOfType = (prefix: string, type: string): SetupDraftFragment =>
  one(rec(`${prefix}_record`, 'Totals', [f('worked', 'Worked out', type as SetupDraftField['type'])]));

const NO_DRAFT_ROLES = 'A setup draft holds records and links only; it has no roles, access or invites.';
const NO_DRAFT_PLACE = 'A setup draft has no part for this.';
const SETUP_HOLDS_NAME: SetupDraftFragment = { records: [] };

/** A formula field over this row. */
const formula = (key: string, label: string, result: 'number' | 'money' | 'quantity' | 'text' | 'boolean', expr: NonNullable<SetupDraftField['formula']>['expr']): SetupDraftField =>
  f(key, label, 'formula', { formula: { result, expr, onMissing: 'blank' } });

const field = (fieldKey: string) => ({ kind: 'field' as const, fieldKey });

/* ----------------------------------------------------------------- words */

/* ------------------------------------------- parts only the studio draft holds */

const MODE = f('mode', 'Paid by', 'select', {
  options: [
    { key: 'cash', label: 'Cash' },
    { key: 'upi', label: 'UPI' },
    { key: 'credit', label: 'Credit' },
  ],
});
const is = (fieldKey: string, value: string): StudioLinkCondition => ({ field: fieldKey, op: 'is', values: [value] });
const CARRY: StudioLinkFill[] = [
  { to: 'date', kind: 'field', field: 'date' },
  { to: 'amount', kind: 'field', field: 'amount' },
];

/** Two records and a studio link from the first into the second. */
const studioLinked = (prefix: string, over: Partial<StudioLink> = {}): StudioDraftFragment => ({
  records: [sales(`${prefix}_sales`, 'Sales', [MODE]), sales(`${prefix}_cash`, 'Cash in hand')],
  links: [{ key: prefix, from: `${prefix}_sales`, to: `${prefix}_cash`, pattern: 'feed', sign: 1, mode: 'auto', when: [], fills: CARRY, ...over }],
});

/** As `studioLinked`, with the sale picking a customer from a list that has terms. */
const viaLinked = (prefix: string, over: Partial<StudioLink> = {}): StudioDraftFragment => ({
  records: [
    sales(`${prefix}_sales`, 'Sales', [f('customer', 'Customer', 'reference', { targetRecordKey: `${prefix}_customers` })]),
    sales(`${prefix}_dues`, 'Dues'),
    list(`${prefix}_customers`, 'Customers', [f('terms', 'Terms', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'credit', label: 'Credit' }] })]),
  ],
  links: [{ key: prefix, from: `${prefix}_sales`, to: `${prefix}_dues`, pattern: 'feed', sign: 1, mode: 'auto', when: [], fills: CARRY, ...over }],
});

/** A record and a role over it. */
const withRole = (prefix: string, name: string, access: StudioRole['access']): StudioDraftFragment => ({
  records: [sales(`${prefix}_sales`, 'Sales')],
  roles: [{ key: prefix, name, people: [], base: 'member', permissions: [], access }],
});

/** A card total of one record's measure. */
const measureOf = (record: string): StudioCardLeaf => ({ kind: 'agg', agg: 'sum', record, of: { kind: 'measure' } });

/** The file an import example reads: any uuid; the store checks the row, never the judge. */
const IMPORT_SOURCE = '5b0f0a6e-8c1d-4a2b-9e3f-1d2c3b4a5e6f';

export const CAPABILITY_WORDS = {
  /* ------------------------------------------------------- the workspace */
  'workspace.name': {
    name: 'Workspace name',
    what: "The business's name, shown on every screen",
    use: 'Sharma Traders; Smile Dental',
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  'workspace.currency': {
    name: 'Currency',
    what: 'The money every amount is in',
    use: 'Rupees for a shop in Pune; dirhams in Dubai',
    also: ['rupees', 'paisa', 'currency'],
    status: 'shipped',
    reach: made(['propose_business']),
    example: SETUP_HOLDS_NAME,
  },
  'workspace.logo': {
    name: 'Logo',
    what: 'A picture for the workspace',
    use: 'On bills and beside the name',
    status: 'shipped',
    reach: after([]),
    unexpressible: 'A logo is picked from the device on a screen, never said in a sentence.',
  },
  'workspace.timezone': {
    name: 'Time zone',
    what: 'Which day today is, for daily totals',
    use: "Today's sales close at midnight where the shop is",
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: "Each device's own clock decides the day; the workspace keeps no zone.",
  },
  'workspace.language': {
    name: 'Language',
    what: 'The language OS speaks in a conversation',
    use: 'Hindi, Tamil, English and more',
    also: ['bhasha', 'hindi', 'language'],
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: true },
    unexpressible: 'The language is chosen per conversation, not kept on the workspace.',
  },

  /* ----------------------------------------------------------- the data */
  'field.type.text': {
    name: 'Text',
    what: 'A short line of words',
    use: 'An item name; a vehicle number',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_text', 'Deliveries', [f('vehicle', 'Vehicle number', 'text')])),
  },
  'field.type.long_text': {
    name: 'Note',
    what: 'A longer piece of writing',
    use: 'Remarks on a visit',
    also: ['remarks', 'note'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_long_text', 'Visits', [f('remarks', 'Remarks', 'long_text')])),
  },
  'field.type.number': {
    name: 'Number',
    what: 'A figure that is not money and not a count',
    use: 'A meter reading; a room number',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_number', 'Readings', [f('reading', 'Meter reading', 'number')])),
  },
  'field.type.money': {
    name: 'Money',
    what: 'An amount, in the workspace currency',
    use: 'A fee; a rate; what was paid',
    also: ['amount', 'paisa', 'rupees', 'price'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_money', 'Fees', [f('fee', 'Fee', 'money')])),
  },
  'field.type.quantity': {
    name: 'Quantity',
    what: 'A count or a measure, with a unit if wanted',
    use: 'Litres of milk; crates; kilos',
    also: ['qty', 'kitna', 'litres', 'kg'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_quantity', 'Milk', [f('litres', 'Litres', 'quantity')])),
  },
  'field.type.date': {
    name: 'Date',
    what: 'A day',
    use: 'The visit date; a due date',
    also: ['tareekh', 'din', 'due date'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_date', 'Bills', [f('due', 'Due date', 'date')])),
  },
  'field.type.datetime': {
    name: 'Date and time',
    what: 'A day with its time',
    use: 'A booking slot; when a truck left',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_datetime', 'Bookings', [f('slot', 'Slot', 'datetime')])),
  },
  'field.type.boolean': {
    name: 'Yes or no',
    what: 'A tick that is on or off',
    use: 'Delivered; paid in full',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('type_boolean', 'Orders', [f('delivered', 'Delivered', 'boolean')])),
  },
  'field.type.select': {
    name: 'Choice',
    what: 'One of a few fixed choices',
    use: 'Cash, UPI or card; small, medium or large',
    ask: 'Is it always one of a few fixed answers?',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(
      rec('type_select', 'Payments', [
        f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }, { key: 'card', label: 'Card' }] }),
      ]),
    ),
  },
  'field.type.multi_select': {
    name: 'Several from a list',
    what: 'Several rows of one list on one entry',
    use: 'The services of one salon visit',
    ask: 'Can one visit have several services?',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('type_multi_select_services', 'Services'),
      rec('type_multi_select_visits', 'Visits', [f('services', 'Services', 'multi_select', { targetRecordKey: 'type_multi_select_services' })]),
    ),
  },
  'field.type.reference': {
    name: 'Pick from a list',
    what: 'One row of a list, picked by name',
    use: 'The customer of a sale; the item sold',
    ask: 'Do you pick the customer from people you already know?',
    also: ['customer', 'party', 'grahak'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(
      list('type_reference_customers', 'Customers'),
      rec('type_reference_sales', 'Sales', [f('customer', 'Customer', 'reference', { targetRecordKey: 'type_reference_customers' })]),
    ),
  },
  'field.type.attachment': {
    name: 'Photo or file',
    what: 'A photo or a file kept on the entry',
    use: 'A photo of the bill',
    also: ['photo', 'bill photo', 'receipt'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('type_attachment', 'Lab bills', [f('photo', 'Bill photo', 'attachment')])),
  },
  'field.type.phone': {
    name: 'Phone',
    what: 'A number to call',
    use: "A customer's phone",
    also: ['mobile', 'number', 'phone'],
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(list('type_phone', 'Customers', [f('phone', 'Phone', 'phone')])),
  },
  'field.type.location': {
    name: 'Place on the map',
    what: 'A spot on the map',
    use: 'The site of a job; where a delivery went',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('type_location', 'Site visits', [f('site', 'Site', 'location')])),
  },
  'field.type.formula': {
    name: 'Worked out',
    what: 'A value worked out from other fields, never typed',
    use: 'Balance = fee less what was paid',
    ask: 'Is any number worked out from the others?',
    also: ['hisaab', 'total', 'balance'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('type_formula', 'Visits', [
        f('fee', 'Fee', 'money'),
        f('paid', 'Paid', 'money'),
        formula('balance', 'Balance', 'money', { kind: 'calc', op: 'subtract', left: field('fee'), right: field('paid') }),
      ]),
    ),
  },
  'field.type.rollup': {
    name: 'Total of other entries',
    what: 'A total of other entries, kept as a field',
    use: 'What a customer has bought in all',
    status: 'no_engine',
    reach: nothing,
    attempt: fieldOfType('type_rollup', 'rollup'),
    cannotYet: { need: 'A field that totals other entries', fit: 'a card that totals the entries, grouped by the row they belong to' },
  },
  'field.type.lookup': {
    name: 'Read through',
    what: 'A value read off another record, kept as a field',
    use: "The customer's area on each sale",
    status: 'no_engine',
    reach: nothing,
    attempt: fieldOfType('type_lookup', 'lookup'),
    cannotYet: { need: 'A field that copies a value from another record', fit: 'a worked-out field that reads it through the reference' },
  },
  'field.type.lines': {
    name: 'Lines',
    what: 'Many rows inside one entry, each with its own values',
    use: 'The medicines on a bill; the rooms of a stay',
    ask: 'Does one bill have many medicines?',
    also: ['items', 'line items', 'bill items', 'saman', 'maal'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('lines_sales', 'Daily sales', [
        f('medicines', 'Medicines', 'lines', {
          lines: {
            noun: 'medicine',
            nouns: 'medicines',
            fields: [
              f('item', 'Item', 'text'),
              f('qty', 'Qty', 'quantity'),
              f('rate', 'Rate', 'money'),
              formula('line_total', 'Amount', 'money', { kind: 'calc', op: 'multiply', left: field('qty'), right: field('rate') }),
            ],
          },
        }),
        formula('total', 'Total', 'money', { kind: 'agg', op: 'sum', lines: 'medicines', of: 'line_total' }),
      ]),
    ),
  },
  'field.option.required': {
    name: 'Must be filled',
    what: 'The entry cannot be saved without it',
    use: 'The amount of a sale',
    status: 'shipped',
    reach: made(['propose_fields'], 'yes'),
    example: one(rec('option_required', 'Sales', [f('amount', 'Amount', 'money', { required: true })])),
  },
  'field.option.sticky': {
    name: 'Keeps the last value',
    what: 'The next entry opens with the value just used',
    use: "Today's route; the same customer again",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_sticky', 'Rounds', [f('route', 'Route', 'text', { sticky: true })])),
  },
  'field.option.help': {
    name: 'Help line',
    what: 'A short line under a field',
    use: '"Fee less what was paid"',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_help', 'Visits', [f('fee', 'Fee', 'money', { help: 'What the visit cost' })])),
  },
  'field.option.shape': {
    name: 'How a field looks',
    what: 'The control a field is filled with',
    use: 'Quick amounts; a stepper; choice buttons',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('option_shape', 'Tips', [f('tip', 'Tip', 'money', { quickAmounts: ['10', '20', '50'] })])),
  },
  'field.option.option_sign_tone': {
    name: 'Choice that counts in or out',
    what: 'Each choice says whether money comes in or goes out, and the colour it wears',
    use: 'Gave or got on a ledger; present in green',
    also: ['gave', 'got', 'diya', 'liya', 'jama', 'naam'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      sales(
        'option_sign_tone',
        'Ledger',
        [
          f('way', 'Gave or got', 'select', {
            options: [
              { key: 'got', label: 'Got', sign: 1, tone: 'positive' },
              { key: 'gave', label: 'Gave', sign: -1, tone: 'negative' },
            ],
          }),
        ],
        { direction: 'both', signFieldKey: 'way' },
      ),
    ),
  },
  'field.option.filter': {
    name: 'Choice narrowed by another',
    what: 'A list that shows only the rows that fit another field',
    use: 'Sub-category picked within the category',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('option_filter_categories', 'Categories'),
      list('option_filter_subs', 'Sub-categories', [f('category', 'Category', 'reference', { targetRecordKey: 'option_filter_categories' })]),
      rec('option_filter_expenses', 'Expenses', [
        f('category', 'Category', 'reference', { targetRecordKey: 'option_filter_categories' }),
        f('sub', 'Sub-category', 'reference', { targetRecordKey: 'option_filter_subs', filter: { field: 'category', equals: 'category' } }),
      ]),
    ),
  },
  'field.option.max_chosen': {
    name: 'Most that can be picked',
    what: 'How many rows one entry may pick from a list',
    use: 'Up to three toppings',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('option_max_chosen_toppings', 'Toppings'),
      rec('option_max_chosen_orders', 'Orders', [f('toppings', 'Toppings', 'multi_select', { targetRecordKey: 'option_max_chosen_toppings', maxChosen: 3 })]),
    ),
  },
  'field.option.starts_with': {
    name: 'Starting value',
    what: 'The value a new entry opens with, which can be typed over',
    use: "The customer's usual rate",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_starts_with', 'Milk', [f('rate', 'Rate', 'money', { startsWith: { result: 'money', expr: { kind: 'const', value: 60 }, onMissing: 'blank' } })]),
    ),
  },
  'field.option.show_when': {
    name: 'Shown only sometimes',
    what: 'A field that shows only when another answer calls for it',
    use: 'Cheque number, only when paid by cheque',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('option_show_when', 'Payments', [
        f('by_cheque', 'Paid by cheque', 'boolean'),
        f('cheque', 'Cheque number', 'text', { showWhen: { result: 'boolean', expr: field('by_cheque'), onMissing: 'blank' } }),
      ]),
    ),
  },
  'record.record': {
    name: 'Record',
    what: 'A place where entries are written each day',
    use: 'Sales, expenses, visits, trips',
    also: ['register', 'bahi', 'khata'],
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('record_record', 'Expenses', [f('amount', 'Amount', 'money')])),
    attempt: {
      records: Array.from({ length: COMPOSE_LIMITS.records + 1 }, (_, at) => rec(`record_over_${at + 1}`, `Record ${at + 1}`, [])),
    },
  },
  'record.list': {
    name: 'List',
    what: 'Names other records pick from',
    use: 'Customers, items, staff, payment modes',
    ask: 'Do you pick the same customers or items again and again?',
    also: ['customers', 'items', 'staff', 'master'],
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(list('record_list', 'Items')),
  },
  'record.direction': {
    name: 'Money in or out',
    what: 'Whether entries add to the total, take from it, or only count',
    use: 'Sales add; expenses take; attendance counts',
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('record_direction', 'Expenses', [f('amount', 'Amount', 'money')], { direction: 'out' })),
  },
  'record.icon': {
    name: 'Icon',
    what: 'The picture a record wears, from what it holds',
    use: 'Stock, people, money',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(list('record_icon', 'Stock items', [], { icon: 'stock' })),
  },
  'record.entry_noun': {
    name: 'Word for one entry',
    what: "The business's own word for one entry",
    use: 'A visit, a bill, a trip',
    status: 'shipped',
    reach: made(['propose_record']),
    example: one(rec('record_entry_noun', 'Trips', [], { entryNoun: 'trip' })),
  },
  'record.seed_rows': {
    name: 'Starting rows',
    what: 'The rows a list starts with',
    use: 'Cash, UPI, card; expense categories',
    status: 'shipped',
    reach: made([]),
    example: one(
      list('record_seed_rows', 'Payment modes', [], {
        seedRows: [{ values: [{ fieldKey: 'name', value: 'Cash' }] }, { values: [{ fieldKey: 'name', value: 'UPI' }] }],
      }),
    ),
  },
  'record.bindings': {
    name: 'What a record answers',
    what: 'Which field names an entry, which is its day, which amount it totals',
    use: 'A sale is named by its customer, dated by its day, totalled by its amount',
    status: 'shipped',
    reach: made(['propose_record']),
    example: one(sales('record_bindings', 'Sales', [], { focusFieldKey: 'amount' })),
  },
  'record.archive': {
    name: 'Leave a record out',
    what: 'Put a record away with its entries, never deleting them',
    use: 'A record the business no longer keeps',
    status: 'shipped',
    reach: made([], 'yes'),
    studioExample: { records: [rec('record_archive', 'Old sales', [])] },
  },
  'record.rename': {
    name: 'Rename a record',
    what: "Change a record's name",
    use: 'Sales becomes Counter sales',
    status: 'shipped',
    reach: made([]),
    studioExample: { records: [rec('record_rename', 'Counter sales', [])] },
  },
  'formula.piece.field': {
    name: 'A field on this entry',
    what: 'A worked-out value reads a field of the same entry',
    use: 'Fee, to work out the balance',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(rec('formula_field', 'Visits', [f('fee', 'Fee', 'money'), formula('copy', 'Charged', 'money', field('fee'))])),
  },
  'formula.piece.hop': {
    name: 'A field through a pick',
    what: 'A worked-out value reads a field of the row a pick points at',
    use: "The item's rate on a sale",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('formula_hop_items', 'Items', [f('pack', 'Pack size', 'number')]),
      rec('formula_hop_sales', 'Sales', [
        f('item', 'Item', 'reference', { targetRecordKey: 'formula_hop_items' }),
        formula('pack', 'Pack size', 'number', { kind: 'hop', via: 'item', fieldKey: 'pack' }),
      ]),
    ),
  },
  'formula.piece.const': {
    name: 'A fixed number',
    what: 'A worked-out value uses a number that never changes',
    use: 'Tax at 18 in a hundred',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_const', 'Bills', [
        f('amount', 'Amount', 'money'),
        formula('tax', 'Tax', 'money', { kind: 'calc', op: 'multiply', left: field('amount'), right: { kind: 'const', value: 0.18 } }),
      ]),
    ),
  },
  'formula.piece.calc': {
    name: 'Add, take, times, divide',
    what: 'A worked-out value adds, takes away, multiplies or divides',
    use: 'Litres times rate',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_calc', 'Milk', [
        f('litres', 'Litres', 'quantity'),
        f('rate', 'Rate', 'money'),
        formula('amount', 'Amount', 'money', { kind: 'calc', op: 'multiply', left: field('litres'), right: field('rate') }),
      ]),
    ),
  },
  'formula.piece.round': {
    name: 'Round',
    what: 'A worked-out value rounded to so many places',
    use: 'A rate rounded to two places',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_round', 'Splits', [
        f('total', 'Total', 'money'),
        formula('share', 'Each share', 'money', {
          kind: 'round',
          decimals: 2,
          of: { kind: 'calc', op: 'divide', left: field('total'), right: { kind: 'const', value: 3 } },
        }),
      ]),
    ),
  },
  'formula.piece.if': {
    name: 'If this, then that',
    what: 'A worked-out value that depends on a test',
    use: 'Late fee only when past the due day',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_if', 'Rent', [
        f('days_late', 'Days late', 'number'),
        formula('late_fee', 'Late fee', 'money', {
          kind: 'if',
          left: field('days_late'),
          op: 'gt',
          right: { kind: 'const', value: 0 },
          then: { kind: 'const', value: 100 },
          otherwise: { kind: 'const', value: 0 },
        }),
      ]),
    ),
  },
  'formula.piece.days_between': {
    name: 'Days between',
    what: 'The number of days from one date to another',
    use: 'Nights of a stay',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_days_between', 'Stays', [
        f('check_in', 'Check in', 'date'),
        f('check_out', 'Check out', 'date'),
        formula('nights', 'Nights', 'number', { kind: 'days_between', from: field('check_in'), to: field('check_out') }),
      ]),
    ),
  },
  'formula.piece.concat': {
    name: 'Join words',
    what: 'Words of several fields joined into one',
    use: 'Room and guest name in one line',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_concat', 'Stays', [
        f('room', 'Room', 'text'),
        f('guest', 'Guest', 'text'),
        formula('label', 'Room and guest', 'text', { kind: 'concat', parts: [field('room'), { kind: 'const', value: ' · ' }, field('guest')] }),
      ]),
    ),
  },
  'formula.piece.coalesce': {
    name: 'Or else',
    what: 'The first of several values that is filled',
    use: "Today's rate, or else the usual one",
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      rec('formula_coalesce', 'Milk', [
        f('rate', 'Rate today', 'money'),
        f('usual', 'Usual rate', 'money'),
        formula('charged', 'Charged', 'money', { kind: 'coalesce', of: [field('rate'), field('usual')] }),
      ]),
    ),
  },
  'formula.piece.agg': {
    name: 'Total of the lines',
    what: 'A total over the lines of an entry, or over the rows it picked',
    use: 'The bill total from its lines',
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('formula_agg_services', 'Services', [f('minutes', 'Minutes', 'number')]),
      rec('formula_agg_visits', 'Visits', [
        f('services', 'Services', 'multi_select', { targetRecordKey: 'formula_agg_services' }),
        formula('minutes', 'Minutes', 'number', { kind: 'agg', op: 'sum', lines: 'services', of: 'minutes' }),
      ]),
    ),
  },

  /* -------------------------------------------------------- the surface */
  'surface.form': {
    name: 'Form',
    what: 'One entry written at a time',
    use: 'Almost every record',
    status: 'shipped',
    reach: made(['propose_record'], 'yes'),
    example: one(rec('surface_form', 'Expenses', [f('amount', 'Amount', 'money')])),
  },
  'surface.sheet': {
    name: 'Sheet',
    what: 'One line per row of a list, one tap each, for one day',
    use: 'Staff attendance; a delivery round',
    ask: 'Is attendance marked for everyone at once?',
    also: ['attendance', 'hazri', 'register'],
    status: 'shipped',
    reach: made(['propose_fields']),
    example: one(
      list('surface_sheet_staff', 'Staff'),
      rec(
        'surface_sheet_attendance',
        'Attendance',
        [
          f('date', 'Date', 'date'),
          f('staff', 'Staff', 'reference', { targetRecordKey: 'surface_sheet_staff' }),
          f('mark', 'Mark', 'select', { options: [{ key: 'present', label: 'Present', tone: 'positive' }, { key: 'absent', label: 'Absent', tone: 'negative' }] }),
        ],
        { headlineFieldKey: 'staff', dateFieldKey: 'date', capture: { mode: 'sheet', overFieldKey: 'staff', cellFieldKey: 'mark', quickOptionKey: 'present' } },
      ),
    ),
  },
  'surface.catalogue': {
    name: 'Catalogue',
    what: 'Items tapped off a menu into one entry',
    use: 'A restaurant order',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no catalogue surface.',
    cannotYet: { need: 'Tapping items off a menu', fit: 'lines on the entry, each picking an item from a list' },
  },
  'surface.counter': {
    name: 'Counter',
    what: 'A running count tapped up and down',
    use: 'Footfall at the door',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no counter surface.',
    cannotYet: { need: 'A tap counter', fit: 'a record with a quantity that starts at one, and a card of the day total' },
  },
  'surface.timer': {
    name: 'Timer',
    what: 'A clock started and stopped on an entry',
    use: 'A salon chair; hours on a job',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no timer surface.',
    cannotYet: { need: 'A start and stop timer', fit: 'a start time and an end time, and the hours worked out' },
  },

  /* ---------------------------------------------------- the arrangement */
  'arrangement.sections': {
    name: 'Sections',
    what: 'Groups of fields on the form, which can fold',
    use: 'Patient details, then treatment, then payment',
    status: 'shipped',
    reach: made(['propose_layout']),
    example: one(
      rec(
        'arrangement_sections',
        'Visits',
        [f('patient', 'Patient', 'text'), f('fee', 'Fee', 'money'), f('notes', 'Notes', 'long_text')],
        {
          layout: [
            { title: 'Visit', style: 'plain', fieldKeys: ['title', 'patient'] },
            { title: 'Payment', style: 'card', fieldKeys: ['fee'] },
            { title: 'More', style: 'folded', fieldKeys: ['notes'] },
          ],
        },
      ),
    ),
  },
  'arrangement.steps': {
    name: 'Steps',
    what: 'A form filled page by page',
    use: 'A long admission form',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no steps.',
    cannotYet: { need: 'A form in pages', fit: 'sections, with the later ones folded' },
  },
  'arrangement.tabs': {
    name: 'Tabs',
    what: 'A form split into tabs',
    use: 'Details and documents of one job',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no tabs.',
    cannotYet: { need: 'A form in tabs', fit: 'sections on one form' },
  },

  /* ------------------------------------------------------ the lifecycle */
  'lifecycle.statuses': {
    name: 'Statuses',
    what: 'The stages an entry moves through',
    use: 'Booked, checked in, checked out',
    also: ['status', 'stage', 'pending', 'done'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no statuses.',
    cannotYet: { need: 'Stages an entry moves through', fit: 'a choice field for the stage, and a card of how many are at each' },
  },
  'lifecycle.approval': {
    name: 'Approval',
    what: 'An entry that waits for someone to approve it',
    use: 'An expense the owner signs off',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no approval step.',
    cannotYet: { need: 'Entries that wait for approval', fit: 'a yes-or-no Approved field, and a card of what is not approved' },
  },
  'lifecycle.reminders': {
    name: 'Reminders',
    what: 'A reminder raised from an entry on a day',
    use: 'Rent due on the fifth; follow up a customer',
    also: ['reminder', 'yaad', 'alert', 'due'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no reminders.',
    cannotYet: { need: 'Reminders on a day', fit: 'a due date on the entry, and a card of what is due or late' },
  },

  /* ----------------------------------------------------------- the view */
  'view.list': {
    name: 'List view',
    what: 'All entries, newest first, grouped by day',
    use: 'Every sale of the day, newest first',
    status: 'shipped',
    reach: made([]),
    example: one(sales('view_list', 'Sales')),
  },
  'view.summary': {
    name: 'Cards on top',
    what: 'The cards on top of a record',
    use: "Today's total, this month against last",
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('view_summary', 'Sales', [], {
        tiles: [
          { label: 'Today', card: 'insight.stat', agg: 'sum', scope: 'day' },
          { label: 'This month', card: 'insight.delta', agg: 'sum', scope: 'month' },
        ],
      }),
    ),
  },
  'view.stored_options': {
    name: 'Saved list options',
    what: 'A saved field order, hidden fields and a filter on the list (hidden_fields, field_order, component_override)',
    use: 'Hide the cost price on the list',
    status: 'stored_not_drawn',
    reach: nothing,
    unexpressible: 'A setup draft writes the list view itself, and no screen reads its saved options.',
    cannotYet: { need: 'A saved order or hidden fields on the list', fit: 'sections on the form, and OS shows a view for now' },
  },
  'view.view_for_now': {
    name: 'A view for now',
    what: 'The list shown a certain way for now, with a way back',
    use: 'Only UPI sales this week',
    status: 'shipped',
    reach: after(['set_view'], false),
    unexpressible: 'A view for now is set in a workspace, never stored by a setup.',
  },
  'view.board': {
    name: 'Board',
    what: 'Entries in columns by their stage',
    use: 'Jobs by status',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no board view.',
    cannotYet: { need: 'A board of entries by stage', fit: 'a choice field for the stage, and a breakdown card by it' },
  },
  'view.calendar': {
    name: 'Calendar',
    what: 'Entries on a calendar by their day',
    use: 'Bookings of the month',
    also: ['calendar', 'booking'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no calendar view.',
    cannotYet: { need: 'A calendar of bookings', fit: 'the list view grouped by day, and a card of the coming week' },
  },
  'view.resource_grid': {
    name: 'Resource grid',
    what: 'Rooms or chairs down the side, days across the top',
    use: 'Which room is booked on which night',
    status: 'planned',
    reach: nothing,
    unexpressible: 'A setup draft has no grid view.',
    cannotYet: { need: 'A grid of rooms by night', fit: 'a list of rooms, stays that pick one, and a card of nights by room' },
  },

  /* -------------------------------------------------------- the insight */
  'insight.card.stat': {
    name: 'Number card',
    what: 'One number: a total, a count, an average',
    use: "Today's sales",
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_stat', 'Sales', [], { tiles: [{ label: 'Today', card: 'insight.stat', agg: 'sum', scope: 'day' }] })),
  },
  'insight.card.delta': {
    name: 'Against last time',
    what: 'This period against the one before',
    use: 'This month against last month',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_delta', 'Sales', [], { tiles: [{ label: 'This month', card: 'insight.delta', agg: 'sum', scope: 'month' }] })),
  },
  'insight.card.progress': {
    name: 'Progress card',
    what: 'This period as a bar against the one before',
    use: 'Collections this week so far',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_progress', 'Collections', [], { tiles: [{ label: 'This week', card: 'insight.progress', agg: 'sum', scope: 'week' }] })),
  },
  'insight.card.trend': {
    name: 'Trend line',
    what: 'One number a day, as a line',
    use: 'Sales over the last two weeks',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_trend', 'Sales', [], { tiles: [{ label: 'Two weeks', card: 'insight.trend', agg: 'sum', days: 14 }] })),
  },
  'insight.card.bars': {
    name: 'Bars by day',
    what: 'One number a day, as bars',
    use: 'Milk collected each day this week',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('card_bars', 'Milk', [], { tiles: [{ label: 'This week', card: 'insight.bars', agg: 'sum', days: 7 }] })),
  },
  'insight.card.breakdown': {
    name: 'Breakdown',
    what: 'One number per group, largest first',
    use: 'Sales by item; expenses by category',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('card_breakdown', 'Sales', [f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }] })], {
        tiles: [{ label: 'By mode', card: 'insight.breakdown', agg: 'sum', scope: 'month', groupByFieldKey: 'mode' }],
      }),
    ),
  },
  'insight.agg': {
    name: 'Total, count, average',
    what: 'A card adds up, counts, averages, or takes the smallest or largest',
    use: 'Average bill size',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('insight_agg', 'Bills', [], { tiles: [{ label: 'Average bill', card: 'insight.stat', agg: 'avg', scope: 'month' }] })),
  },
  'insight.scope': {
    name: 'Period',
    what: 'The day, week, month, year or all time a card reads',
    use: 'This week',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(sales('insight_scope', 'Sales', [], { tiles: [{ label: 'This year', card: 'insight.stat', agg: 'sum', scope: 'year' }] })),
  },
  'insight.where': {
    name: 'Only some entries',
    what: 'A card counts only the entries that meet a few conditions',
    use: 'Only cash sales',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('insight_where', 'Sales', [f('mode', 'Paid by', 'select', { options: [{ key: 'cash', label: 'Cash' }, { key: 'upi', label: 'UPI' }] })], {
        tiles: [{ label: 'Cash today', card: 'insight.stat', agg: 'sum', scope: 'day', where: [{ fieldKey: 'mode', op: 'is', optionKey: 'cash' }] }],
      }),
    ),
  },
  'insight.where_list': {
    name: 'Any of, none of',
    what: 'A card condition that matches any of several choices, or none of them',
    use: 'Cash or UPI sales',
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_where_list_sales', 'Sales', [MODE])],
      boardCards: [
        {
          key: 'insight_where_list',
          label: 'Cash or UPI today',
          card: 'insight.stat',
          scope: 'day',
          total: { ...measureOf('insight_where_list_sales'), where: [{ field: 'mode', op: 'in', value: ['cash', 'upi'] }] },
        },
      ],
    },
  },
  'insight.group_by': {
    name: 'By group',
    what: 'A card split by a field',
    use: 'Sales by item',
    status: 'shipped',
    reach: made(['propose_card']),
    example: one(
      sales('insight_group_by', 'Expenses', [f('kind', 'Kind', 'select', { options: [{ key: 'rent', label: 'Rent' }, { key: 'power', label: 'Power' }] })], {
        direction: 'out',
        tiles: [{ label: 'By kind', card: 'insight.breakdown', agg: 'sum', scope: 'month', groupByFieldKey: 'kind', limit: 5 }],
      }),
    ),
  },
  'insight.via': {
    name: 'Through a pick',
    what: 'A card condition or group read through the row a pick points at',
    use: "Sales by the customer's area",
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_via_sales', 'Sales', [f('customer', 'Customer', 'reference', { targetRecordKey: 'insight_via_customers' })]), list('insight_via_customers', 'Customers', [f('area', 'Area', 'text')])],
      boardCards: [
        {
          key: 'insight_via',
          label: 'Sold in Kothrud',
          card: 'insight.stat',
          scope: 'month',
          total: { ...measureOf('insight_via_sales'), where: [{ field: 'area', op: 'is', value: 'Kothrud', via: 'customer' }] },
        },
      ],
    },
  },
  'insight.row_recipe': {
    name: 'Worked out per entry',
    what: 'A card that totals a value worked out per entry',
    use: 'Total of litres times rate',
    status: 'shipped',
    reach: after(['propose_card']),
    unexpressible: 'A setup card totals one field; it holds no recipe.',
  },
  'insight.cross_record': {
    name: 'Across records',
    what: 'Totals joined across records, on the Dashboard',
    use: 'What each customer owes: credit less payments, by customer',
    ask: 'Do you want to see what each customer owes across bills and payments?',
    also: ['udhaar', 'baki', 'outstanding', 'owes', 'lena dena'],
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_cross_sales', 'Sales'), sales('insight_cross_costs', 'Costs', [], { direction: 'out' })],
      boardCards: [
        {
          key: 'insight_cross_record',
          label: 'Left this month',
          card: 'insight.stat',
          scope: 'month',
          total: { kind: 'calc', op: 'subtract', left: measureOf('insight_cross_sales'), right: measureOf('insight_cross_costs') },
        },
      ],
    },
  },
  'insight.dashboard': {
    name: 'Dashboard card',
    what: 'A card on the Dashboard, across the workspace',
    use: 'Cash in hand today',
    status: 'shipped',
    reach: made(['propose_card']),
    studioExample: {
      records: [sales('insight_dashboard_sales', 'Sales')],
      boardCards: [{ key: 'insight_dashboard', label: 'Sold today', card: 'insight.stat', scope: 'day', total: measureOf('insight_dashboard_sales') }],
    },
  },
  'insight.rows_count': {
    name: 'Count of entries',
    what: 'How many entries, rather than how many filled a field',
    use: 'Visits this month',
    status: 'shipped',
    reach: after(['propose_card']),
    unexpressible: 'A setup count counts the filled values of one field.',
  },

  /* ----------------------------------------------------- the automation */
  'link.pattern.feed': {
    name: 'Feed link',
    what: 'A saved entry posts a new entry into another record',
    use: 'A cash sale posts into Cash in hand',
    ask: 'When you write a sale, is it written somewhere else too?',
    status: 'shipped',
    reach: made(['propose_link']),
    example: linked('link_feed'),
  },
  'link.pattern.settle': {
    name: 'Settle against bills',
    what: 'A payment closed against particular open bills',
    use: 'A payment that clears two old bills',
    also: ['settle', 'adjust', 'clear'],
    status: 'no_engine',
    reach: nothing,
    attempt: linkWithPattern('link_settle', 'settle'),
    cannotYet: {
      need: 'Settle a payment against particular bills',
      fit: 'a Payments record feeding Udhaar as Got, and a card of what each owes',
    },
  },
  'link.pattern.rollup': {
    name: 'Rollup link',
    what: 'A total of entries kept on another record',
    use: 'Monthly sales kept on a summary record',
    status: 'no_engine',
    reach: nothing,
    attempt: linkWithPattern('link_rollup', 'rollup'),
    cannotYet: { need: 'A total kept on another record', fit: 'a card that totals the entries for the period' },
  },
  'link.pattern.balance': {
    name: 'Balance link',
    what: 'A running balance kept on a row of a list',
    use: "A customer's balance on their row",
    status: 'no_engine',
    reach: nothing,
    attempt: linkWithPattern('link_balance', 'balance'),
    cannotYet: { need: 'A running balance kept on a row', fit: "a card that totals each customer's entries" },
  },
  'link.pattern.reminder': {
    name: 'Reminder link',
    what: 'A saved entry raises a reminder',
    use: 'A due date raises a reminder',
    status: 'no_engine',
    reach: nothing,
    attempt: linkWithPattern('link_reminder', 'reminder'),
    cannotYet: { need: 'A reminder raised by an entry', fit: 'a due date on the entry, and a card of what is due' },
  },
  'link.mode.auto': {
    name: 'Runs by itself',
    what: 'A link posts as soon as the entry is saved',
    use: 'Every cash sale reaches Cash in hand',
    status: 'shipped',
    reach: made(['propose_link']),
    example: linked('link_auto'),
  },
  'link.mode.paused': {
    name: 'Paused link',
    what: 'A link kept but not posting, until it is turned on',
    use: 'Start the link paused until you check it',
    status: 'shipped',
    reach: made(['propose_link', 'propose_link_change']),
    studioExample: studioLinked('link_paused', { mode: 'paused' }),
  },
  'link.fill.field': {
    name: 'Copy a field',
    what: 'A link fills a field with a field of the saved entry',
    use: 'The amount and the day go across',
    status: 'shipped',
    reach: made(['propose_link']),
    example: linked('link_fill_field'),
  },
  'link.fill.const': {
    name: 'A fixed value',
    what: 'A link fills a field with the same value every time',
    use: 'Every posted row says Sale',
    status: 'shipped',
    reach: made(['propose_link']),
    example: linked('link_fill_const', {
      carry: [
        { toFieldKey: 'date', fromFieldKey: 'date' },
        { toFieldKey: 'amount', fromFieldKey: 'amount' },
        { toFieldKey: 'title', const: 'Sale' },
      ],
    }),
  },
  'link.fill.reference': {
    name: 'Read through a pick',
    what: 'A link fills a field with a value read off the row a pick points at',
    use: "Post the bill to stock at the item's price",
    status: 'shipped',
    reach: made(['propose_link']),
    studioExample: viaLinked('link_fill_reference', { fills: [...CARRY, { to: 'title', kind: 'reference', via: 'customer', field: 'name' }] }),
  },
  'link.fill.formula': {
    name: 'Worked out on the way',
    what: 'A link fills a field with a value worked out from the saved entry',
    use: 'Litres times the usual rate',
    status: 'shipped',
    reach: made(['propose_link']),
    studioExample: studioLinked('link_fill_formula', {
      fills: [
        { to: 'date', kind: 'field', field: 'date' },
        { to: 'amount', kind: 'formula', formula: { result: 'money', expr: { kind: 'calc', op: 'multiply', left: field('amount'), right: { kind: 'const', value: 2 } }, onMissing: 'blank' } },
      ],
    }),
  },
  'link.and': {
    name: 'Conditions, all of them',
    what: 'A link posts only when every condition holds',
    use: 'Only cash sales over 500',
    status: 'shipped',
    reach: made(['propose_link']),
    example: linked('link_and', {
      when: [
        { fieldKey: 'mode', op: 'is', value: 'cash' },
        { fieldKey: 'amount', op: 'at_least', value: 500 },
      ],
    }),
  },
  'link.or': {
    name: 'Conditions, any of them',
    what: 'Conditions as an AND of ORs',
    use: 'When paid by cash or UPI, post it to Cash in hand',
    status: 'shipped',
    reach: made(['propose_link']),
    studioExample: studioLinked('link_or', { when: [{ any: [is('mode', 'cash'), is('mode', 'upi')] }] }),
  },
  'link.via_condition': {
    name: 'Condition through a pick',
    what: 'A link condition asked of the row a pick points at',
    use: "Only when the customer's terms are credit",
    status: 'shipped',
    reach: made(['propose_link']),
    studioExample: viaLinked('link_via', { when: [{ any: [{ field: 'terms', op: 'is', values: ['credit'], via: 'customer' }] }] }),
  },
  'link.into_list': {
    name: 'Link into a list',
    what: 'A link that adds or changes a row of a list',
    use: 'A new customer added from a sale',
    status: 'no_engine',
    reach: nothing,
    attempt: {
      records: [sales('link_into_list_sales', 'Sales'), list('link_into_list_customers', 'Customers')],
      links: [
        {
          fromRecordKey: 'link_into_list_sales',
          toRecordKey: 'link_into_list_customers',
          pattern: 'feed',
          sign: 1,
          when: [],
          carry: [{ toFieldKey: 'name', fromFieldKey: 'title' }],
        },
      ],
    },
    cannotYet: { need: 'A link into a list', fit: 'a link runs between two records, never a directory; keep the movements in a record of their own' },
  },
  'link.reads_lines': {
    name: 'Link that reads lines',
    what: 'A link that posts once per line of an entry',
    use: 'Stock down for each item on a bill',
    also: ['stock', 'maal', 'inventory'],
    status: 'no_engine',
    reach: nothing,
    unexpressible: 'A setup link reads the fields of the saved entry, never its lines.',
    cannotYet: { need: 'A link that reads lines (stock down per line)', fit: 'a card of stock received beside items sold' },
  },
  'link.match_open_row': {
    name: 'Land on an open row',
    what: 'A link that updates an entry already open instead of posting a new one',
    use: 'A payment that closes the open bill',
    status: 'no_engine',
    reach: nothing,
    unexpressible: 'A link always posts a new entry.',
    cannotYet: { need: 'A link that updates an open entry', fit: 'a new entry in a payments record, and a card of what is still open' },
  },

  /* --------------------------------------------------------- the people */
  'people.group.records': {
    name: 'Rights over records',
    what: 'Who may see, add, change, delete and download entries',
    use: 'Counter staff add sales but never delete them',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.insights': {
    name: 'Rights over cards',
    what: 'Who may see and change cards and the Dashboard',
    use: 'Only the owner sees the profit card',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.links': {
    name: 'Rights over links',
    what: 'Who may see and change links',
    use: 'Only the manager changes links',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.team': {
    name: 'Rights over the team',
    what: 'Who may invite, remove people, and change roles and access',
    use: 'The manager invites new staff',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.workspace': {
    name: 'Rights over the workspace',
    what: 'Who may change the workspace and download all of it',
    use: 'Only the owner downloads everything',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.group.os': {
    name: 'Rights over OS',
    what: 'Who may ask OS, let it make changes, talk live and keep facts',
    use: 'Staff ask OS but cannot let it change the setup',
    status: 'shipped',
    reach: after(['propose_role', 'propose_role_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.owner': {
    name: 'Owner',
    what: 'The person who makes the workspace, with every right',
    use: 'The shop owner',
    status: 'shipped',
    reach: made([]),
    example: SETUP_HOLDS_NAME,
  },
  'people.role': {
    name: 'Roles',
    what: 'A named set of rights; entry reach off, own, their team, everyone',
    use: 'Counter writes sales; Delivery sees home deliveries only',
    ask: 'Who else writes in it, and what should each of them see?',
    also: ['staff', 'worker', 'accountant', 'manager', 'naukar', 'munim'],
    status: 'shipped',
    reach: made(['propose_role', 'propose_role_change', 'propose_role_copy', 'propose_role_remove']),
    studioExample: withRole('people_role', 'Counter staff', []),
  },
  'people.access': {
    name: 'Access per record',
    what: 'Who may see or change a record, or one field of it',
    use: 'Hide the cost price from staff',
    status: 'shipped',
    reach: made(['propose_grant']),
    studioExample: withRole('people_access', 'Sales staff', [{ record: 'people_access_sales', level: 'see' }]),
  },
  'people.invite': {
    name: 'Invitations',
    what: 'Ask a person to join, by a link or a link and a code',
    use: 'Invite the accountant',
    also: ['invite', 'add staff', 'join'],
    status: 'shipped',
    reach: after(['propose_invite', 'propose_invites', 'propose_invite_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.reports_to': {
    name: 'Reports to',
    what: 'Who is in whose team',
    use: 'Delivery staff report to the manager',
    status: 'shipped',
    reach: after(['propose_member_change']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.handover': {
    name: 'Hand over',
    what: 'Make someone else the main admin of the workspace',
    use: 'The owner hands the shop to a son',
    status: 'shipped',
    reach: after(['propose_handover', 'propose_handover_answer']),
    unexpressible: NO_DRAFT_ROLES,
  },
  'people.ask_right': {
    name: 'Ask for a right',
    what: 'A member asks for more access, and the owner answers',
    use: 'Staff ask to see the totals',
    status: 'shipped',
    reach: after(['propose_ask', 'propose_ask_answer']),
    unexpressible: NO_DRAFT_ROLES,
  },

  /* --------------------------------------------------------- the memory */
  'memory.fact': {
    name: 'Facts',
    what: 'Sentences OS remembers about the business',
    use: 'We close on Tuesdays',
    status: 'shipped',
    reach: after(['propose_fact', 'propose_fact_change'], false),
    unexpressible: 'A setup draft holds parts, not facts; the setup conversation keeps them.',
  },
  'memory.setup_thread': {
    name: 'Setup conversation',
    what: 'The setup conversation, kept as the first thread of the workspace',
    use: 'Carry on where setup left off',
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: 'The conversation is kept beside the draft, never inside it.',
  },
  'memory.website': {
    name: 'Website',
    what: "The business's own website, read for its name, items, prices, phones and hours",
    use: 'Read the menu off the website',
    also: ['website', 'site'],
    status: 'shipped',
    reach: after(['read_website'], false),
    unexpressible: 'A website is read beside the draft, never inside it.',
  },
  'memory.unmet_need': {
    name: 'Noted wish',
    what: 'A note of something the parts cannot hold yet',
    use: 'They want to send customers reminders',
    status: 'shipped',
    reach: after(['note_unmet_need'], false),
    unexpressible: 'A wish is noted beside the draft, never drawn in it.',
  },

  /* ---------------------------------------------------------- the input */
  'input.file.sheet': {
    name: 'Spreadsheet',
    what: 'A spreadsheet file OS reads',
    use: 'A price list; last month of sales',
    also: ['sheet', 'xlsx', 'spreadsheet'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.csv': {
    name: 'CSV file',
    what: 'Comma-separated rows OS reads',
    use: 'An export from another tool',
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.tally': {
    name: 'Accounting export',
    what: 'An accounting export in XML that OS reads',
    use: 'Parties and ledgers from the accounts',
    also: ['xml', 'ledger', 'accounts'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.pdf': {
    name: 'PDF',
    what: 'A PDF OS reads',
    use: 'A printed rate card',
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.image': {
    name: 'Photo',
    what: 'A photo OS reads',
    use: 'A photo of a register page',
    also: ['photo', 'picture', 'register photo'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.website': {
    name: 'Website page',
    what: 'A page of a website, kept like a file',
    use: 'The menu page of the website',
    status: 'shipped',
    reach: after(['read_website'], false),
    unexpressible: 'A website is read beside the draft, never inside it.',
  },
  'input.file.xls': {
    name: 'Older spreadsheet',
    what: 'An older spreadsheet file (.xls) OS reads',
    use: 'Last year of sales from an old workbook',
    also: ['xls', 'old spreadsheet'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.docx': {
    name: 'Word document',
    what: 'A Word document OS reads',
    use: 'A price list typed in a document',
    also: ['docx', 'word', 'document'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.pptx': {
    name: 'PowerPoint',
    what: 'Slides OS reads',
    use: 'A rate card made as slides',
    also: ['pptx', 'slides'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.json': {
    name: 'JSON export',
    what: 'An export in JSON that OS reads',
    use: 'The data exported from another tool',
    also: ['json'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.xml': {
    name: 'XML export',
    what: 'An export in XML that OS reads',
    use: 'Items exported from another tool',
    also: ['xml'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.text': {
    name: 'Text',
    what: 'Plain text or tab-separated rows OS reads',
    use: 'A list pasted from a notes app',
    also: ['txt', 'tsv', 'text'],
    status: 'shipped',
    reach: after(['read_file'], false),
    unexpressible: 'A file is read beside the draft, never inside it.',
  },
  'input.file.place': {
    name: 'Map place',
    what: 'A place on a map, kept like a file',
    use: 'The shop on the map',
    also: ['map', 'place'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A file is read beside the draft, never inside it.',
    cannotYet: { need: 'Keep a map place as a source', fit: 'say the address and hours in the conversation' },
  },
  'input.file.page': {
    name: 'Web page',
    what: 'One public page, kept like a file',
    use: 'The business page of the shop',
    also: ['page', 'link'],
    status: 'planned',
    reach: nothing,
    unexpressible: 'A file is read beside the draft, never inside it.',
    cannotYet: { need: 'Keep one public page as a source', fit: 'say what the page shows in the conversation' },
  },
  'input.import': {
    name: 'Import',
    what: "A file's rows written into records by a job",
    use: 'Three hundred customers from a spreadsheet',
    also: ['import', 'upload', 'data'],
    status: 'shipped',
    reach: made(['propose_import']),
    studioExample: {
      records: [list('input_import_customers', 'Customers')],
      sources: [{ id: IMPORT_SOURCE, role: 'mine', dismissed: false }],
      imports: [
        {
          key: 'input_import',
          source: IMPORT_SOURCE,
          sheet: 'Customers',
          record: 'input_import_customers',
          headerRow: 0,
          skipRows: [],
          columns: [{ column: 0, header: 'Name', field: 'name', transform: { kind: 'text' } }],
        },
      ],
    },
  },
  'input.entries_from_file': {
    name: 'Entries from a file',
    what: 'A few rows of a file written as entries',
    use: "Last week's sales from a photo",
    status: 'shipped',
    reach: after(['propose_entries'], false),
    unexpressible: 'A setup never writes an entry.',
  },
  'input.web_search': {
    name: 'Web search',
    what: 'OS looks up how a trade works',
    use: 'How a dairy pays its farmers',
    status: 'shipped',
    reach: { setup: 'no', edit: 'no', os: [], screen: false },
    unexpressible: 'Research happens beside the draft, never inside it.',
  },
  'input.place': {
    name: 'Place lookup',
    what: "A business's place read from a map link",
    use: 'The address and hours of the shop',
    status: 'planned',
    reach: nothing,
    unexpressible: NO_DRAFT_PLACE,
    cannotYet: { need: 'Read a place from a map link', fit: 'ask the person for the address and hours' },
  },
  'input.app_research': {
    name: 'Looking up the app you use',
    what: 'OS reads how the app the person uses now works',
    use: 'Set up the same records as the app they use',
    status: 'planned',
    reach: nothing,
    unexpressible: NO_DRAFT_PLACE,
    cannotYet: { need: 'Look up how the app you use works', fit: 'ask what they keep in it today, record by record' },
  },

  /* ------------------------------------------------------- not possible yet */
  'not_yet.send_messages': {
    name: 'Send messages',
    what: 'Messages or reminders sent to customers',
    use: 'Remind customers who owe',
    also: ['message', 'sms', 'reminder', 'bhejna', 'bhejo', 'customers', 'remind'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part sends anything outside the app.',
    cannotYet: { need: 'Send messages to customers', fit: 'a Due date on the entry and a card of what is over 30 days' },
  },
  'not_yet.barcode_scanning': {
    name: 'Barcode scanning',
    what: 'Items found by scanning their barcode at checkout or stock in',
    use: 'Scan items into a bill',
    also: ['barcode', 'scan', 'scanner', 'scanning', 'qr', 'sku'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part reads a barcode or a scanner.',
    cannotYet: { need: 'Barcode scanning', fit: 'pick the item from the Items list by name, its usual rate filled in' },
  },
  'not_yet.gst_filing': {
    name: 'GST filing',
    what: 'Tax returns filed from the entries',
    use: 'File GST each month',
    also: ['gst', 'tax', 'return', 'filing'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part files anything with the government.',
    cannotYet: { need: 'GST filing', fit: 'a GST field and a monthly tax card' },
  },
  'not_yet.payroll_payment': {
    name: 'Pay salaries',
    what: 'Salaries paid out from the app',
    use: 'Pay staff at the month end',
    also: ['salary', 'payroll', 'tankhwah', 'pagar'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part moves money.',
    cannotYet: { need: 'Payroll payments', fit: 'attendance and a salary card' },
  },
  'not_yet.bank_feed': {
    name: 'Bank feed',
    what: 'Bank entries arriving by themselves',
    use: 'Every deposit shows up on its own',
    also: ['bank', 'statement', 'passbook'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'No part connects to a bank.',
    cannotYet: { need: 'A bank feed', fit: 'a Bank deposits record entered by hand or imported from a statement file' },
  },
  'not_yet.stock_from_lines': {
    name: 'Stock from bill lines',
    what: 'Stock falling as each line of a bill is saved',
    use: 'Stock down for every item sold',
    also: ['stock', 'inventory', 'maal', 'godown'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'A link never reads the lines of an entry.',
    cannotYet: { need: "Stock falling as a bill's lines are saved", fit: 'a card of stock received beside items sold' },
  },
  'not_yet.settle_against_bills': {
    name: 'Settle against bills',
    what: 'A payment set against particular bills',
    use: 'This payment clears bills 12 and 14',
    also: ['udhaar', 'khata', 'baki', 'settle'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'A settle link is stored but writes nothing.',
    cannotYet: {
      need: 'Settle a payment against particular bills',
      fit: 'a Payments record feeding Udhaar as Got, and a card of what each owes',
    },
  },
  'not_yet.balance_on_party': {
    name: 'Balance on a party',
    what: "A running balance kept on a customer's row",
    use: "Each customer's balance on their row",
    also: ['balance', 'baki', 'khata', 'udhaar'],
    status: 'not_in_recordos',
    reach: nothing,
    unexpressible: 'A balance link is stored but writes nothing.',
    cannotYet: { need: "A balance kept on a party's row", fit: "a card that totals each customer's entries" },
  },
  'not_yet.third_lines_level': {
    name: 'A third level of lines',
    what: 'Lines inside lines inside lines',
    use: 'Rooms of a stay, guests of a room, meals of a guest',
    status: 'not_in_recordos',
    reach: nothing,
    attempt: one(
      rec('third_lines', 'Stays', [
        f('rooms', 'Rooms', 'lines', {
          lines: {
            noun: 'room',
            nouns: 'rooms',
            fields: [
              f('room', 'Room', 'text'),
              f('guests', 'Guests', 'lines', {
                lines: {
                  noun: 'guest',
                  nouns: 'guests',
                  fields: [
                    f('guest', 'Guest', 'text'),
                    f('meals', 'Meals', 'lines', { lines: { noun: 'meal', nouns: 'meals', fields: [f('meal', 'Meal', 'text')] } }),
                  ],
                },
              }),
            ],
          },
        }),
      ]),
    ),
    cannotYet: { need: 'A third level of lines', fit: 'a separate record that picks the second level' },
  },
} satisfies Record<CapabilityId, CapabilityWords>;
